import { FormattedMessage } from 'react-intl-next';

import { type Entitlement } from '../blocks/subscriptionV2/SubscriptionDetails';

export const supportUrl: string = 'https://support.atlassian.com';

export const subscriptionAndBillingSupportUrl: string =
	'https://support.atlassian.com/subscriptions-and-billing/resources/';

export const privacyPolicyUrl: string = 'https://www.atlassian.com/legal/privacy-policy';

export function getSupportUrl(): string {
	return supportUrl;
}

export function getSubscriptionAndBillingSupportUrl(): string {
	return subscriptionAndBillingSupportUrl;
}

export function getSubscriptionAndBillingRovoDevSupportUrl(): string {
	return 'https://support.atlassian.com/subscriptions-and-billing/docs/how-billing-works-for-rovo-dev-standard ';
}

export function getPrivacyPolicyUrl(): string {
	return privacyPolicyUrl;
}

export function getPersonalDataDeletionUrl(): string {
	return 'https://www.atlassian.com/trust/privacy/gdpr/request-delete-personal-data';
}

export function getCancelSubscriptionUrl(): string {
	return 'https://support.atlassian.com/subscriptions-and-billing/docs/cancel-a-subscription/';
}

export function getDataRetentionAndBackupUrl(): string {
	return 'https://support.atlassian.com/security-and-access-policies/docs/track-storage-and-move-data-across-products/';
}

export function getMeteredBillingUsageUrl(): string {
	return 'https://support.atlassian.com/subscriptions-and-billing/docs/how-does-usage-affect-my-bill';
}

export function getAdminHubUrl(envType: string) {
	if (envType === 'Development') {
		return 'https://admin.dev.atlassian.com';
	} else if (envType === 'Staging') {
		return 'https://admin.stg.atlassian.com';
	} else {
		return 'https://admin.atlassian.com';
	}
}

export function getRemoveUsersUrl(envType: string, orgId?: string) {
	const orgRemoveUsersFilter = orgId ? `/o/${orgId}/users` : '';

	const adminHubUrl = getAdminHubUrl(envType);

	return adminHubUrl.concat(orgRemoveUsersFilter);
}

export function getBillingConsoleUrl(envType: string) {
	return `${getAdminHubUrl(envType)}/billing`;
}

export function getHAMSBillingConsoleUrl(envType: string, cloudId: string) {
	return `${getAdminHubUrl(envType)}/s/${cloudId}/billing/overview`;
}

export function getBillingConsoleManageSubscriptionUrl(
	transactionAccountId: string,
	entitlementId: string,
	envType: string,
) {
	return `${getBillingConsoleUrl(envType)}/${transactionAccountId}/entitlement/${entitlementId}`;
}

export function getBillingConsoleUsageDetailsUrl(
	transactionAccountId: string,
	entitlementId: string,
	envType: string,
	chargeElementName: string,
) {
	return `${getBillingConsoleManageSubscriptionUrl(transactionAccountId, entitlementId, envType)}/manage-usage/${chargeElementName}`;
}

export function getBillingConsoleChangePlanUrl(
	transactionAccountId: string,
	entitlementId: string,
	envType: string,
) {
	return `${getBillingConsoleUrl(envType)}/${transactionAccountId}/entitlement/${entitlementId}/change-plan`;
}

export function getBillingAdminPermissionUrl(
	envType: string,
	transactionAccountId: string,
	taskId: string,
) {
	return `${getBillingConsoleUrl(envType)}/${transactionAccountId}/permissions?taskId=${taskId}`;
}

export function getMACUrl(envType: string) {
	if (envType === 'Development') {
		return 'https://my.dev.atl-paas.net';
	} else if (envType === 'Staging') {
		return 'https://my.stg.internal.atlassian.com';
	} else {
		return 'https://my.atlassian.com';
	}
}

export function getPartnerAccountUrl(envType: string) {
	if (envType === 'Development') {
		return 'https://ppc.dev.atlassian.com';
	} else if (envType === 'Staging') {
		return 'https://ppc.stg.atlassian.com';
	} else {
		return 'https://ppc.atlassian.com';
	}
}

export function getContactUsLink() {
	return 'https://www.atlassian.com/company/contact';
}

export function getContactUsLinkForPurchasingAndLicensing() {
	return 'https://www.atlassian.com/company/contact/purchasing-licensing#/';
}

export function getPartnerSupportUrl(envType: string) {
	if (envType === 'Staging') {
		return 'https://partners.stg.internal.atlassian.com';
	} else {
		return 'https://atlassianpartners.atlassian.net';
	}
}
export function getPartnerManagedSubscriptionUrl(envType: string, entitlementId?: string) {
	if (entitlementId) {
		return `${getAdminHubUrl(envType)}/partner-managed-subscriptions/${entitlementId}`;
	}
	return `${getAdminHubUrl(envType)}/partner-managed-subscriptions`;
}

export function getPPCBaseUrl(envType: string) {
	if (envType === 'Development') {
		return 'https://ppc.dev.atlassian.com';
	} else if (envType === 'Staging') {
		return 'https://ppc.stg.atlassian.com';
	}
	return 'https://ppc.atlassian.com';
}

export function getPPCCartUrl(transactionAccountId: string, envType: string) {
	return `${getPPCBaseUrl(envType)}/account/${transactionAccountId}/cart`;
}

export function getPPCSubscriptionUrl(
	transactionAccountId: string,
	entitlementId: string,
	envType: string,
) {
	return `${getPPCBaseUrl(envType)}/account/${transactionAccountId}/subscriptions/${entitlementId}`;
}

export function getAdvocateCentralUrl(envType: string) {
	if (envType === 'Staging') {
		return 'https://advocatecentral.staging.atl-paas.net';
	} else {
		return ' https://advocatecentral.prod.atl-paas.net';
	}
}

export function getCASSIUrl(envType: string) {
	if (envType === 'Development') {
		return 'https://cassi.dev.internal.atlassian.com';
	} else if (envType === 'Staging') {
		return 'https://cassi.stg.internal.atlassian.com';
	} else {
		return 'https://cassi.internal.atlassian.com';
	}
}

export function getWACUrl(envType: string) {
	if (envType === 'Development') {
		return 'https://cart.dev.atl-paas.net';
	} else if (envType === 'Staging') {
		return 'https://wac.stg.internal.atlassian.com';
	} else {
		return 'https://www.atlassian.com';
	}
}

export function getMACQuotesUrl(envType: string) {
	return getWACUrl(envType) + '/purchase/my/billing/quotes';
}

export function getQuoteUrl(accountId: string, quoteId: string, envType: string) {
	if (envType === 'Staging') {
		return `https://ppc.stg.atlassian.com/account/${accountId}/quotes/${quoteId}`;
	} else {
		return `https://ppc.atlassian.com/account/${accountId}/quotes/${quoteId}`;
	}
}

export function getSupportTicketUrl(supportTicket: string, envType: string) {
	if (envType === 'Staging') {
		return `https://getsupport.stg.internal.atlassian.com/browse/${supportTicket}`;
	} else {
		return `https://getsupport.atlassian.com/browse/${supportTicket}`;
	}
}

export function getManageSubscriptionUrl(transactionAccountId: string, envType: string) {
	return `${getAdminHubUrl(envType)}/billing/${transactionAccountId}`;
}

export function getStartedUrl(
	orderId: string,
	transactionAccountId: string,
	envType: string,
	isDirectBuy?: boolean,
) {
	if (isDirectBuy) {
		return `${getWACUrl(envType)}/buy/cloud/post-order-provisioning?orderId=${orderId}&transactionAccount=${transactionAccountId}&directBuyStatus=postProvisioning`;
	}
	return `${getWACUrl(envType)}/buy/cloud/post-order-provisioning?orderId=${orderId}&transactionAccount=${transactionAccountId}`;
}

export function removeHttp(url: string) {
	return url.replace(/^https?:\/\//, '');
}

type BillingLinkProps = Partial<{
	isHipChat: boolean;
	isBitbucket: boolean;
	isSen: boolean;
	isOrganization: boolean;
	sen: string;
	external_uuid: string;
	domainName: string;
	add: boolean;
	update: boolean;
	transactionAccountId: string;
	invoiceGroupId: string;
	isPartner: boolean;
	referrer: string;
}> & { envType: string };

export const getBillingLink = ({
	isHipChat,
	isBitbucket,
	isSen,
	isOrganization,
	sen,
	external_uuid: externalUuid,
	domainName,
	add,
	transactionAccountId,
	invoiceGroupId,
	isPartner,
	referrer,
	envType,
}: BillingLinkProps) => {
	let paymentFlowLink;
	if (isHipChat) {
		return `https://${domainName?.replace(/^https:\/\//, '')}/admin/purchase/`;
	}
	if (isBitbucket || isSen) {
		return `${getMACUrl(envType)}/products?sen=${sen}`;
	}
	let operation = 'add';
	if (!add) {
		operation = 'update';
	}
	if (isOrganization) {
		return `${getAdminHubUrl(envType)}/o/${externalUuid}/billing/paymentdetails/${operation}`;
	}

	if (transactionAccountId && invoiceGroupId) {
		if (isPartner) {
			return `${getPartnerAccountUrl(envType)}/account/${transactionAccountId}/billing-profile/${invoiceGroupId}`;
		}
		if (domainName) {
			paymentFlowLink = `${domainName}/billing/${transactionAccountId}/${invoiceGroupId}/payment-flow`;
		} else {
			paymentFlowLink = `${getAdminHubUrl(envType)}/billing/${transactionAccountId}/${invoiceGroupId}/payment-flow`;
		}

		if (referrer) {
			return `${paymentFlowLink}?referrer=${referrer}`;
		}
		return paymentFlowLink;
	}

	if (transactionAccountId) {
		paymentFlowLink = `${domainName}/billing/${transactionAccountId}/payment-methods`;
		if (referrer) {
			return `${paymentFlowLink}?referrer=${referrer}`;
		}
		return paymentFlowLink;
	}

	return `https://${domainName?.replace(/^https:\/\//, '')}/admin/billing/paymentdetails/${operation}`;
};

export function getAtlassianProductsUrl() {
	return 'https://www.atlassian.com/software';
}

export function getAtlassianMarketplaceUrl() {
	return 'https://marketplace.atlassian.com/';
}

const ZERO = 0;
export const join = (items: string[]): string | null => {
	return items && items.length > ZERO ? items.join(', ') : null;
};

export function getFirstName(fullName: string) {
	return fullName.split(' ')[0];
}

export const formatAmount = (amount: number, currency: string, shouldFlipSign: boolean) => {
	const amountToFormat = amount ? (shouldFlipSign ? -amount : amount) : '0.00';
	const amountCurrency = currency ? currency : 'USD';

	return Number(amountToFormat).toLocaleString('en-US', {
		style: 'currency',
		currency: amountCurrency,
		currencyDisplay: 'code',
	});
};

export function populateIfOfferingIsDifferent(
	newPricingPlan?: string,
	hasDifferentOffering?: boolean,
) {
	return hasDifferentOffering ? (
		<FormattedMessage
			id="commerce.commerce-subscription-skip-trial-ccp.email.body.on-pricing-plan"
			defaultMessage=" on {newPricingPlan}."
			description="On new pricing plan"
			values={{
				newPricingPlan: newPricingPlan,
			}}
		/>
	) : (
		'.'
	);
}

export function getPartnerServiceDeskUrl() {
	return 'https://atlassianpartners.atlassian.net/support#/?inquiry_category=partner_sales_support&partner_sales_support_topic=quotes_and_trials&reason=need_quote';
}

export const getEntitlementId = (entitlementId: string, entitlements: Entitlement[]) => {
	if (!entitlements) {
		return entitlementId;
	}
	return entitlements[0] ? entitlements[0].entitlementId : entitlementId;
};

export const getProductName = (productName: string, entitlements: Entitlement[]) => {
	if (!entitlements) {
		return productName;
	}
	return entitlements[0] ? entitlements[0].productName : productName;
};

export const getOfferingName = (offeringName: string, entitlements: Entitlement[]) => {
	if (!entitlements) {
		return offeringName;
	}
	return entitlements[0] ? entitlements[0].offeringName : offeringName;
};

export const getOldOfferingName = (oldOfferingName: string, entitlements: Entitlement[]) => {
	if (!entitlements) {
		return oldOfferingName;
	}
	return entitlements[0] ? entitlements[0].oldOfferingName : oldOfferingName;
};

export const getDeactivationMonth = (trialEndDate: string) => {
	const formattedDate = new Date(trialEndDate);
	formattedDate.setDate(formattedDate.getDate() + 14);

	return formattedDate.toLocaleDateString('en-US', { month: 'long' });
};

export const getDeactivationDay = (trialEndDate: string) => {
	const formattedDate = new Date(trialEndDate);
	formattedDate.setDate(formattedDate.getDate() + 14);

	return formattedDate.getDate();
};
